if (!customElements.get('cart-gift-milestone')) {
  customElements.define(
    'cart-gift-milestone',
    class CartGiftMilestone extends HTMLElement {
      static syncing = false;

      connectedCallback() {
        window.requestAnimationFrame(() => this.reconcile());
      }

      get unlocked() {
        return this.dataset.unlocked === 'true';
      }

      get giftKey() {
        return this.dataset.giftKey || '';
      }

      get giftQuantity() {
        return Number.parseInt(this.dataset.giftQuantity || '0', 10);
      }

      /* Cles des lignes en trop du produit cadeau : le panier n en garde
         qu une seule, sinon la commande part avec plusieurs savons. */
      get extraKeys() {
        return (this.dataset.extraKeys || '').split(',').filter(Boolean);
      }

      /* theme.routes donne les adresses de formulaire (/cart/add, /cart/update).
         Postees telles quelles, elles renvoient la page du panier en HTML, que
         la lecture JSON ne peut pas interpreter : la mise a jour echouait donc
         a chaque fois, et le panier n etait jamais rafraichi. Les versions en
         .js repondent en JSON, qui est ce que ce composant attend. */
      route(url, defaut) {
        const base = url || defaut;
        return base.endsWith('.js') ? base : base + '.js';
      }

      get statusElement() {
        return this.querySelector('[data-gift-status]');
      }

      /* Le seuil calcule par le theme porte sur tout le panier, alors que la
         remise automatique peut ne viser qu'une partie du catalogue. Quand le
         cadeau est dans le panier sans que Shopify lui applique sa remise, il
         serait facture au client alors qu'on vient de lui annoncer un cadeau :
         on le retire, et on ne le repropose plus de la session. */
      get discountApplied() {
        return this.dataset.discountApplied !== 'false';
      }

      get refuseKey() {
        return 'nigelle_gift_unavailable_' + this.dataset.variantId;
      }

      get abandonne() {
        try { return sessionStorage.getItem(this.refuseKey) === '1'; }
        catch (e) { return false; }
      }

      set abandonne(valeur) {
        try { sessionStorage.setItem(this.refuseKey, valeur ? '1' : '0'); }
        catch (e) { /* navigation privee : on continue sans memoire */ }
      }

      async reconcile() {
        if (CartGiftMilestone.syncing || !this.dataset.variantId) return;

        const extraKeys = this.extraKeys;

        const factureAtort = this.unlocked && Boolean(this.giftKey) && !this.discountApplied;
        if (factureAtort) {
          CartGiftMilestone.syncing = true;
          this.abandonne = true;
          try {
            const updates = { [this.giftKey]: 0 };
            extraKeys.forEach((key) => { updates[key] = 0; });
            await this.updateCart(updates);
            if (this.statusElement) {
              this.statusElement.textContent = 'Ce cadeau n’est pas disponible pour ce panier.';
            }
          }
          catch (error) { console.error('Cart reward error:', error); }
          finally { CartGiftMilestone.syncing = false; }
          return;
        }

        const needsGift = this.unlocked && !this.giftKey && !this.abandonne;
        const needsRemoval = !this.unlocked && Boolean(this.giftKey);
        /* La quantite ne se normalise que sur une ligne existante : sans cette
           garde, la cle vide partait dans la requete et Shopify renvoyait une
           erreur 422, qui interrompait la mise a jour du panier. */
        const needsNormalization = this.unlocked && Boolean(this.giftKey) && this.giftQuantity !== 1;
        const needsCleanup = extraKeys.length > 0;

        if (!this.unlocked) this.abandonne = false;

        if (!needsGift && !needsRemoval && !needsNormalization && !needsCleanup) return;

        CartGiftMilestone.syncing = true;
        this.setAttribute('aria-busy', 'true');

        try {
          if (needsGift) await this.addGift();

          const updates = {};
          extraKeys.forEach((key) => { updates[key] = 0; });
          if (needsRemoval) updates[this.giftKey] = 0;
          if (needsNormalization) updates[this.giftKey] = 1;

          await this.updateCart(updates);
        }
        catch (error) {
          console.error('Cart reward error:', error);
          if (this.statusElement) {
            this.statusElement.textContent = 'Le cadeau n’a pas pu être actualisé. Veuillez réessayer.';
          }
        }
        finally {
          this.removeAttribute('aria-busy');
          CartGiftMilestone.syncing = false;
        }
      }

      async addGift() {
        const response = await fetch(this.route(theme.routes.cart_add_url, '/cart/add'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{
              id: Number.parseInt(this.dataset.variantId, 10),
              quantity: 1,
              properties: { _maison_nigelle_gift: 'true' }
            }]
          })
        });

        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Gift add failed');
        }
      }

      async updateCart(updates) {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections }
        }));

        const body = {
          sections,
          sections_url: window.location.pathname
        };
        if (Object.keys(updates).length > 0) body.updates = updates;

        const response = await fetch(this.route(theme.routes.cart_update_url, '/cart/update'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        });

        const parsedState = await response.json();
        if (!response.ok || parsedState.status || parsedState.errors) {
          throw new Error(parsedState.description || parsedState.message || parsedState.errors || 'Cart update failed');
        }

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-reward-gift',
          cart: parsedState
        });
      }

    }
  );
}
